import { useState, useEffect } from 'react';
import { Plus, Star, User, TrendingUp, Target, FileText, ChevronRight, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { api } from '../../core/apiClient';
import { formatDate } from '../../lib/utils';
import { ExcelFilterDropdown } from '../../components/ui/excel-filter';

interface Employee {
  id: string;
  code: string;
  firstName: string;
  lastName: string;
}

interface Evaluation {
  id: string;
  employeeId: string;
  employee?: Employee;
  evaluatorId: string;
  evaluator?: Employee;
  type: string;
  period: string;
  evaluationDate: string;
  performanceScore: number;
  qualityScore: number;
  teamworkScore: number;
  initiativeScore: number;
  punctualityScore: number;
  overallScore: number;
  strengths?: string;
  areasToImprove?: string;
  objectives?: string;
  comments?: string;
  status: string;
}

const EVALUATION_TYPES = [
  { value: 'ANUAL', label: 'Avaluació Anual' },
  { value: 'SEMESTRAL', label: 'Avaluació Semestral' },
  { value: 'PROVA', label: 'Fi Període de Prova' },
  { value: 'EXTRAORDINARIA', label: 'Extraordinària' },
];

export default function Evaluations() {
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEval, setEditingEval] = useState<Evaluation | null>(null);

  const [filters, setFilters] = useState({
    employeeId: '',
    year: new Date().getFullYear(),
    type: '',
  });

  const [formData, setFormData] = useState({
    employeeId: '',
    type: 'ANUAL',
    period: `${new Date().getFullYear()}`,
    evaluationDate: new Date().toISOString().split('T')[0],
    performanceScore: 3,
    qualityScore: 3,
    teamworkScore: 3,
    initiativeScore: 3,
    punctualityScore: 3,
    strengths: '',
    areasToImprove: '',
    objectives: '',
    comments: '',
  });

  const [stats, setStats] = useState({
    totalEvaluations: 0,
    avgScore: 0,
    pending: 0,
    completed: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadEvaluations();
  }, [filters]);

  const loadData = async () => {
    try {
      const response = await api.get<any>('/employees');
      setEmployees(response.data?.data || []);
    } catch (error) {
      console.error('[v0] Error loading employees:', error);
    }
  };

  const loadEvaluations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.employeeId) params.append('employeeId', filters.employeeId);
      if (filters.year) params.append('year', String(filters.year));
      if (filters.type) params.append('type', filters.type);

      const response = await api.get<any>(`/evaluations?${params.toString()}`);
      const data = response.data?.data || [];
      setEvaluations(data);

      // Calculate stats
      const completed = data.filter((e: Evaluation) => e.status === 'COMPLETADA').length;
      const avgScore = data.length > 0
        ? data.reduce((sum: number, e: Evaluation) => sum + Number(e.overallScore), 0) / data.length
        : 0;

      setStats({
        totalEvaluations: data.length,
        avgScore: Math.round(avgScore * 10) / 10,
        pending: data.filter((e: Evaluation) => e.status === 'PENDENT').length,
        completed,
      });
    } catch (error) {
      console.error('[v0] Error loading evaluations:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateOverallScore = () => {
    const scores = [
      formData.performanceScore,
      formData.qualityScore,
      formData.teamworkScore,
      formData.initiativeScore,
      formData.punctualityScore,
    ];
    return Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        overallScore: calculateOverallScore(),
      };

      if (editingEval) {
        await api.put(`/evaluations/${editingEval.id}`, payload);
      } else {
        await api.post('/evaluations', payload);
      }

      setShowModal(false);
      setEditingEval(null);
      resetForm();
      loadEvaluations();
    } catch (error) {
      console.error('[v0] Error saving evaluation:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      employeeId: '',
      type: 'ANUAL',
      period: `${new Date().getFullYear()}`,
      evaluationDate: new Date().toISOString().split('T')[0],
      performanceScore: 3,
      qualityScore: 3,
      teamworkScore: 3,
      initiativeScore: 3,
      punctualityScore: 3,
      strengths: '',
      areasToImprove: '',
      objectives: '',
      comments: '',
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 4) return 'text-green-600';
    if (score >= 3) return 'text-blue-600';
    if (score >= 2) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBadge = (score: number) => {
    if (score >= 4) return <Badge className="bg-green-100 text-green-800">Excel·lent</Badge>;
    if (score >= 3) return <Badge variant="secondary">Bo</Badge>;
    if (score >= 2) return <Badge className="bg-amber-100 text-amber-800">Acceptable</Badge>;
    return <Badge className="bg-red-100 text-red-800">Millorable</Badge>;
  };

  const renderStars = (score: number, onChange?: (value: number) => void) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            onClick={() => onChange?.(star)}
            className={`${onChange ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
            disabled={!onChange}
          >
            <Star
              className={`w-5 h-5 ${star <= score ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/rrhh')}
            title="Tornar enrere"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Avaluacions de Rendiment</h1>
            <p className="text-slate-500">Avaluacions periòdiques dels empleats</p>
          </div>
        </div>
        <Button onClick={() => { resetForm(); setEditingEval(null); setShowModal(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Avaluació
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Avaluacions</p>
                <p className="text-2xl font-bold">{stats.totalEvaluations}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Puntuació Mitjana</p>
                <p className={`text-2xl font-bold ${getScoreColor(stats.avgScore)}`}>
                  {stats.avgScore}/5
                </p>
              </div>
              <Star className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Completades</p>
                <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pendents</p>
                <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
              </div>
              <Target className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="rounded-xl">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Any</Label>
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                value={filters.year}
                onChange={(e) => setFilters(prev => ({ ...prev, year: parseInt(e.target.value) }))}
              >
                {[2024, 2025, 2026, 2027].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Empleat</Label>
              <ExcelFilterDropdown
                label="Empleat"
                options={employees.map(emp => ({ value: emp.id, label: `${emp.firstName} ${emp.lastName}` }))}
                selected={filters.employeeId ? filters.employeeId.split(',') : []}
                onApply={(values) => setFilters(prev => ({ ...prev, employeeId: values.join(',') }))}
                className="w-full"
              />
            </div>
            <div>
              <Label>Tipus</Label>
              <ExcelFilterDropdown
                label="Tipus"
                options={EVALUATION_TYPES.map(type => ({ value: type.value, label: type.label }))}
                selected={filters.type ? filters.type.split(',') : []}
                onApply={(values) => setFilters(prev => ({ ...prev, type: values.join(',') }))}
                className="w-full"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="rounded-xl">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Empleat</TableHead>
                <TableHead>Tipus</TableHead>
                <TableHead>Període</TableHead>
                <TableHead>Data</TableHead>
                <TableHead className="text-center">Puntuació</TableHead>
                <TableHead>Valoració</TableHead>
                <TableHead className="text-right">Accions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">Carregant...</TableCell>
                </TableRow>
              ) : evaluations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No hi ha avaluacions registrades
                  </TableCell>
                </TableRow>
              ) : (
                evaluations.map(evaluation => (
                  <TableRow key={evaluation.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        {evaluation.employee?.firstName} {evaluation.employee?.lastName}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {EVALUATION_TYPES.find(t => t.value === evaluation.type)?.label || evaluation.type}
                      </Badge>
                    </TableCell>
                    <TableCell>{evaluation.period}</TableCell>
                    <TableCell>{formatDate(evaluation.evaluationDate)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-2">
                        <span className={`font-bold ${getScoreColor(Number(evaluation.overallScore))}`}>
                          {Number(evaluation.overallScore).toFixed(1)}
                        </span>
                        {renderStars(Math.round(Number(evaluation.overallScore)))}
                      </div>
                    </TableCell>
                    <TableCell>{getScoreBadge(Number(evaluation.overallScore))}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => {
                        setEditingEval(evaluation);
                        setFormData({
                          employeeId: evaluation.employeeId,
                          type: evaluation.type,
                          period: evaluation.period,
                          evaluationDate: evaluation.evaluationDate,
                          performanceScore: Number(evaluation.performanceScore),
                          qualityScore: Number(evaluation.qualityScore),
                          teamworkScore: Number(evaluation.teamworkScore),
                          initiativeScore: Number(evaluation.initiativeScore),
                          punctualityScore: Number(evaluation.punctualityScore),
                          strengths: evaluation.strengths || '',
                          areasToImprove: evaluation.areasToImprove || '',
                          objectives: evaluation.objectives || '',
                          comments: evaluation.comments || '',
                        });
                        setShowModal(true);
                      }}>
                        Veure
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEval ? 'Detall Avaluació' : 'Nova Avaluació'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Empleat *</Label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                  value={formData.employeeId}
                  onChange={(e) => setFormData(prev => ({ ...prev, employeeId: e.target.value }))}
                  required
                >
                  <option value="">Selecciona empleat</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.code} - {emp.firstName} {emp.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Tipus *</Label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                >
                  {EVALUATION_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Període</Label>
                <Input
                  value={formData.period}
                  onChange={(e) => setFormData(prev => ({ ...prev, period: e.target.value }))}
                  placeholder="2026 / 2026-S1"
                />
              </div>
              <div>
                <Label>Data Avaluació *</Label>
                <Input
                  type="date"
                  value={formData.evaluationDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, evaluationDate: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="font-medium">Puntuacions (1-5)</h3>
              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center justify-between">
                  <Label>Rendiment</Label>
                  {renderStars(formData.performanceScore, (v) => setFormData(prev => ({ ...prev, performanceScore: v })))}
                </div>
                <div className="flex items-center justify-between">
                  <Label>Qualitat del Treball</Label>
                  {renderStars(formData.qualityScore, (v) => setFormData(prev => ({ ...prev, qualityScore: v })))}
                </div>
                <div className="flex items-center justify-between">
                  <Label>Treball en Equip</Label>
                  {renderStars(formData.teamworkScore, (v) => setFormData(prev => ({ ...prev, teamworkScore: v })))}
                </div>
                <div className="flex items-center justify-between">
                  <Label>Iniciativa</Label>
                  {renderStars(formData.initiativeScore, (v) => setFormData(prev => ({ ...prev, initiativeScore: v })))}
                </div>
                <div className="flex items-center justify-between">
                  <Label>Puntualitat</Label>
                  {renderStars(formData.punctualityScore, (v) => setFormData(prev => ({ ...prev, punctualityScore: v })))}
                </div>
              </div>
              <div className="pt-2 border-t flex items-center justify-between">
                <span className="font-medium">Puntuació Global</span>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold ${getScoreColor(calculateOverallScore())}`}>
                    {calculateOverallScore()}/5
                  </span>
                  {getScoreBadge(calculateOverallScore())}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label>Punts Forts</Label>
                <Textarea
                  value={formData.strengths}
                  onChange={(e) => setFormData(prev => ({ ...prev, strengths: e.target.value }))}
                  rows={2}
                />
              </div>
              <div>
                <Label>Àrees de Millora</Label>
                <Textarea
                  value={formData.areasToImprove}
                  onChange={(e) => setFormData(prev => ({ ...prev, areasToImprove: e.target.value }))}
                  rows={2}
                />
              </div>
              <div>
                <Label>Objectius pel Proper Període</Label>
                <Textarea
                  value={formData.objectives}
                  onChange={(e) => setFormData(prev => ({ ...prev, objectives: e.target.value }))}
                  rows={2}
                />
              </div>
              <div>
                <Label>Comentaris Addicionals</Label>
                <Textarea
                  value={formData.comments}
                  onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel·lar
              </Button>
              <Button type="submit">
                {editingEval ? 'Desa' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
